import type { FocusEvent } from 'react';
import React, { forwardRef, useMemo, useState } from 'react';

import { formatAmount, validateNumberInput } from '@/shared/libs/utils';
import type { INumberFilterParams } from '@shared/libs/utils/filter';

import type { FormInputProps } from '../FormInput';
import { FormInput } from '../FormInput';

export interface FormAmountInputProps extends Omit<FormInputProps, 'type'> {
  postfix?: string;
  prefix?: string;
  filterNumberParams?: INumberFilterParams;
}

export const FormAmountInput = forwardRef<
  HTMLInputElement,
  FormAmountInputProps
>(
  (
    { filterNumberParams, onBlur, onFocus, postfix, prefix, value, ...rest },
    ref,
  ) => {
    const [isFocus, setIsFocus] = useState<boolean>(false);

    const curValue = useMemo(() => {
      if (isFocus) {
        return String(value ?? '');
      }

      if (value === undefined || value === null || value === '') {
        return '';
      }

      const formatted = formatAmount(Number(value));

      return postfix ? `${formatted} ${postfix}` : formatted;
    }, [isFocus, value, postfix]);

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
      onFocus?.(e);
      setIsFocus(true);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
      setIsFocus(false);
    };

    return (
      <FormInput
        ref={ref}
        prependSlot={prefix && <span className="text-sm">{prefix}</span>}
        value={curValue}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyPress={validateNumberInput(filterNumberParams ?? {})}
        {...rest}
      />
    );
  },
);

FormAmountInput.displayName = 'FormAmountInput';
